import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import { toast } from 'sonner';
import {
  AlertTriangle,
  AppWindow,
  ArrowLeftRight,
  BadgeCheck,
  BadgeDollarSign,
  BookOpen,
  BookUser,
  Boxes,
  Briefcase,
  Building2,
  CalendarDays,
  CalendarRange,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Contact,
  CreditCard,
  DraftingCompass,
  Factory,
  FileSignature,
  FileText,
  Files,
  Folder,
  FolderKanban,
  FolderOpen,
  FolderPlus,
  Forklift,
  Gauge,
  GitCompareArrows,
  Globe,
  GraduationCap,
  Grid3x3,
  GripVertical,
  Hammer,
  HandCoins,
  Handshake,
  IdCard,
  Inbox,
  KeyRound,
  Landmark,
  LayoutDashboard,
  Layers,
  LineChart,
  ListTodo,
  Megaphone,
  MessageSquareQuote,
  MapPin,
  MessageCircleWarning,
  MonitorSmartphone,
  MoreHorizontal,
  Network,
  Package,
  Palette,
  Palmtree,
  Pencil,
  Plug,
  Printer,
  ReceiptText,
  RefreshCcw,
  Repeat,
  Scale,
  ScanBarcode,
  ScrollText,
  Settings2,
  Share2,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Tag,
  Target,
  Trash2,
  Truck,
  TrendingUp,
  Undo2,
  Users,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/AuthProvider';
import {
  canApprovePto,
  canRead,
  isSuperAdmin,
  vis,
  type Role,
  type SectionAccess,
} from '@/lib/permissions';
import { Logo } from '@/components/brand/Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  useCreateProjectFolder,
  useDeleteProjectFolder,
  useMoveProjectToFolder,
  useProjectFolders,
  useProjects,
  useRenameProjectFolder,
  useReorderProjects,
  type ProjectFolder,
  type ProjectWithMembers,
} from '@/features/projects/hooks';
import { useItemKinds, type ItemKindColor } from '@/features/inventory/hooks';
import { useEngProjects } from '@/features/engineering/hooks';
import { useHasOnlineAccounts } from '@/features/onlineAccounts/hooks';
import { useTickets, useTicketViews, useTicketsRealtime } from '@/features/tickets/hooks';
import { EQUIPMENT_CATEGORIES, EQUIPMENT_CATEGORY_META } from '@/features/equipment/model';

const COLLAPSED_STORAGE_KEY = 'atlas:sidebar:collapsedSections';
const COLLAPSED_ITEMS_STORAGE_KEY = 'atlas:sidebar:collapsedItems';

function readCollapsedFromStorage(): Set<string> {
  return readStringSetFromStorage(COLLAPSED_STORAGE_KEY);
}

function readCollapsedItemsFromStorage(): Set<string> {
  return readStringSetFromStorage(COLLAPSED_ITEMS_STORAGE_KEY);
}

/** Tiny shared parser so the section + per-item state stays in sync
 *  on shape. Treats any non-string entries as missing rather than
 *  crashing on a manually-edited / partially-migrated localStorage
 *  payload. */
function readStringSetFromStorage(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((v): v is string => typeof v === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

/** Per-person facts some nav items depend on beyond role and section grants. */
interface NavContext {
  /** Super admin, or at least one online account has been shared with them. */
  hasOnlineAccounts: boolean;
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  visible: (
    role: Role | null,
    sectionAccess?: SectionAccess | null,
    ctx?: NavContext,
  ) => boolean;
  /**
   * Optional render hook for collapsible nested sub-items shown
   * directly underneath this row. Used today by "Items" to surface
   * one row per item Kind so an operator can jump straight into a
   * scoped view of (e.g.) "Raw materials" without crawling the
   * faceted filter. When set, the sidebar renders a chevron toggle
   * on the row that expands / collapses the children block; the
   * expansion state persists across reloads keyed by the item's
   * `to` path.
   */
  renderChildren?: () => React.ReactNode;
  /**
   * Optional trailing badge (a live count, for instance). Rendered at
   * the right edge of the row, inside the link.
   */
  renderBadge?: () => React.ReactNode;
  /** Match the route exactly (NavLink `end`); `pathMatchesNav` still handles nested detail pages. */
  end?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
  /** Optional render hook to append dynamic items below the static ones. */
  renderExtras?: () => React.ReactNode;
  /**
   * Extra paths that count as "inside" this section for auto-expansion
   * even though no static item matches them — e.g. /projects/:id task
   * lists, which only appear in the dynamic tree rendered by
   * `renderExtras`. Row highlighting is unaffected; only the section
   * un-collapses so the active dynamic row is visible.
   */
  matchesPath?: (pathname: string) => boolean;
}

function pathMatchesNav(to: string, pathname: string): boolean {
  // The print log has its own nav row; don't light "Engineering projects" for it.
  if (to === '/engineering' && pathname.startsWith('/engineering/prints')) return false;
  if (pathname === to || pathname.startsWith(`${to}/`)) return true;
  // Digital Company used to live at /floor (and /digital-office before that).
  if (to === '/digital-company' && (pathname === '/floor' || pathname === '/digital-office')) {
    return true;
  }
  return false;
}

const NAV: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, visible: vis('dashboard') },
      { to: '/calendar', label: 'Calendar', icon: CalendarDays, visible: vis('calendar') },
      { to: '/digital-company', label: 'Digital Company', icon: Building2, visible: vis('digital_office') },
    ],
  },
  // HR — people-ops home. PTO lives under a collapsible parent (My /
  // Manage); org chart, reviews, handbook, personal docs, and a
  // confidential complaints channel cover the rest. Payroll stays in
  // Gusto and is intentionally absent.
  {
    label: 'HR',
    items: [
      {
        to: '/hr/pto',
        label: 'PTO',
        icon: Palmtree,
        visible: vis('pto'),
        // My PTO + Manage PTO — same render-children plumbing as Loans.
        // Manage stays gated to approvers (mirrors App.tsx + migration 95).
        renderChildren: () => <PtoExtras />,
      },
      {
        to: '/hr/employees',
        label: 'Employees',
        icon: Users,
        visible: (r) => canApprovePto(r),
      },
      {
        to: '/hr/org-chart',
        label: 'Org Chart',
        icon: Network,
        visible: vis('hr'),
      },
      {
        to: '/hr/reviews',
        label: 'Employee Reviews',
        icon: Star,
        visible: vis('hr'),
      },
      {
        to: '/hr/handbook',
        label: 'Handbook & Policies',
        icon: BookOpen,
        visible: vis('hr'),
      },
      {
        to: '/hr/documents',
        label: 'Personal Documents',
        icon: IdCard,
        visible: vis('hr'),
      },
      {
        to: '/hr/complaints',
        label: 'Complaints',
        icon: MessageCircleWarning,
        visible: vis('hr'),
      },
    ],
  },
  // Tasks — two halves. "Ticket inbox" is requests received from other
  // people (migration 143; role-only module so it survives revoking the
  // section). "My tasks" is the hub for your own to-dos plus a dynamic
  // sub-list of every task list (project) you can read, across
  // departments. The dynamic half is rendered by ProjectsExtras() below;
  // keeping it inside the section keeps the auto-expand-active-section
  // UX consistent. Department sections (Engineering, Marketing, …) can
  // later mount their own filtered <ProjectsExtras department="…" />
  // while this hub keeps showing everything.
  {
    label: 'Tasks',
    items: [
      {
        to: '/tickets',
        label: 'Ticket inbox',
        icon: Inbox,
        visible: vis('tickets'),
        renderBadge: () => <TicketInboxBadge />,
      },
      {
        to: '/tasks',
        label: 'My tasks',
        icon: ListTodo,
        visible: vis('projects'),
      },
    ],
    renderExtras: () => <ProjectsExtras />,
    // Task lists live at /projects/:id (department-agnostic URL) and
    // only appear in the dynamic tree, so match them here.
    matchesPath: (pathname) => pathname.startsWith('/projects/'),
  },
  // Engineering — projects with their CAD file drive, 3D print log,
  // costs and a tie-in to task lists in the Tasks section. Primarily for
  // the engineering team but readable org-wide (section grant), so a
  // production operator can jump from a component to its files.
  {
    label: 'Engineering',
    items: [
      {
        to: '/engineering',
        label: 'Engineering projects',
        icon: DraftingCompass,
        visible: vis('engineering'),
        end: true,
      },
      {
        to: '/engineering/prints',
        label: '3D print log',
        icon: Printer,
        visible: vis('engineering'),
      },
    ],
    renderExtras: () => <EngineeringExtras />,
  },
  {
    label: 'Financials',
    items: [
      // Cardholder self-service surface — fill in explanation / receipt /
      // category, then submit for approval. Hidden from super_admin: they
      // do the same workflow (and more) from the Transactions page.
      { to: '/financials/expenses', label: 'My expenses', icon: ReceiptText, visible: (r, a) => vis('expenses')(r, a) && !isSuperAdmin(r) },
      // The exec workbook: sales drivers → P&L → cash, plus balance sheet,
      // cash flow, AR/AP aging and insights, with QuickBooks actuals beside
      // every projection. First because it's the page finance opens daily.
      { to: '/financials/projections', label: 'Main Financials', icon: TrendingUp, visible: vis('financials') },
      { to: '/financials/bank-accounts', label: 'Accounts', icon: Wallet, visible: vis('financials') },
      { to: '/financials/transactions', label: 'Transactions', icon: ArrowLeftRight, visible: vis('financials') },
      // Amazon Business — sits under Transactions because it's the
      // line-item-enrichment layer on top of Plaid charges for purchases
      // made on Amazon Business. (ROADMAP §1.7, migration 61.)
      { to: '/financials/amazon-business', label: 'Amazon Business', icon: ShoppingCart, visible: vis('financials') },
      { to: '/financials/reconciliation', label: 'Reconciliation', icon: GitCompareArrows, visible: vis('financials') },
      // Cross-instrument rollup (loans + credit cards + QBO liabilities).
      // Gated on `financials`, not `loans`, because it exposes bank
      // credit balances that external lenders must not see.
      { to: '/financials/debt-balances', label: 'Debt balances', icon: Scale, visible: vis('financials') },
      {
        to: '/financials/loans',
        label: 'Loans',
        icon: HandCoins,
        // Gated on the dedicated `loans` module (NOT `financials`) so
        // external lenders can see Loans without unlocking bank
        // balances, P&L, etc. Mirrors the route gate in App.tsx and
        // the storage-RLS split in migration 60.
        visible: vis('loans'),
        // Sub-rows (Family / SBA) are rendered through the same
        // collapsible-children plumbing as Items → kinds. Each child
        // carries its own visibility predicate (see LOAN_SUB_ITEMS
        // below) so e.g. the SBA row stays super_admin-only while
        // Family is visible to any loan-module reader.
        renderChildren: () => <LoansExtras />,
      },
    ],
  },
  {
    label: 'Catalog',
    items: [
      {
        to: '/catalog/items',
        label: 'Items',
        icon: Package,
        visible: vis('catalog'),
        // One sub-row per item Kind, fetched live from the org's
        // kinds catalog so a super admin's brand-new kind appears
        // here without a code change. See `ItemKindsExtras` below.
        renderChildren: () => <ItemKindsExtras />,
      },
      {
        to: '/catalog/equipment',
        label: 'Equipment',
        icon: Forklift,
        visible: vis('catalog'),
        renderChildren: () => <EquipmentCategoryExtras />,
      },
      { to: '/catalog/customers', label: 'Customers', icon: Contact, visible: (r, a) => vis('catalog')(r, a) && vis('sales')(r, a) },
      { to: '/catalog/suppliers', label: 'Suppliers', icon: Factory, visible: (r, a) => vis('catalog')(r, a) && vis('procurement')(r, a) },
      {
        to: '/catalog/software',
        label: 'Software',
        icon: AppWindow,
        // Accounts are shared per person, so someone without the Catalog
        // section still sees Software once an account is shared with them.
        visible: (r, a, ctx) => vis('catalog')(r, a) || !!ctx?.hasOnlineAccounts,
        renderChildren: () => <SoftwareExtras />,
      },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { to: '/inventory/levels', label: 'On-hand', icon: Boxes, visible: vis('inventory') },
      { to: '/inventory/locations', label: 'Locations', icon: MapPin, visible: vis('inventory') },
      { to: '/inventory/movements', label: 'Stock movements', icon: RefreshCcw, visible: vis('inventory') },
      { to: '/inventory/adjustments', label: 'Adjustments', icon: SlidersHorizontal, visible: vis('inventory') },
    ],
  },
  {
    label: 'Procurement',
    items: [
      { to: '/procurement/calendar', label: 'Procurement calendar', icon: CalendarDays, visible: vis('procurement') },
      { to: '/procurement/purchase-orders', label: 'Purchase orders', icon: ClipboardList, visible: vis('procurement') },
      { to: '/procurement/receiving', label: 'Receiving', icon: Truck, visible: vis('procurement') },
      { to: '/procurement/vendor-invoices', label: 'Vendor invoices', icon: ReceiptText, visible: vis('procurement') },
    ],
  },
  {
    label: 'Manufacturing',
    items: [
      {
        to: '/mfg/production',
        label: 'Production',
        icon: Factory,
        visible: vis('manufacturing'),
        renderChildren: () => <ProductionExtras />,
      },
      { to: '/mfg/scrap-log', label: 'Scrap log', icon: Trash2, visible: vis('manufacturing') },
      // Workstations is the manager side of Work Mode (live benches, people,
      // defects, feedback, instructions); Work Mode itself is the full-screen
      // station tablet UI (no app shell). Work Mode is gated on its own
      // role-only `work_mode` flag so it stays visible to a floor employee
      // even when their Manufacturing section grant is switched off.
      { to: '/mfg/workstations', label: 'Workstations', icon: Hammer, visible: vis('manufacturing') },
      { to: '/work', label: 'Work Mode', icon: MonitorSmartphone, visible: vis('work_mode') },
      { to: '/mfg/boms', label: 'BOMs', icon: Layers, visible: vis('manufacturing') },
      { to: '/mfg/work-schedules', label: 'Work schedules', icon: Settings2, visible: vis('manufacturing') },
      { to: '/mfg/work-orders', label: 'Work orders', icon: ClipboardList, visible: vis('manufacturing') },
      { to: '/mfg/planning', label: 'Production planning', icon: CalendarRange, visible: vis('manufacturing') },
      { to: '/mfg/quality', label: 'Quality', icon: ShieldCheck, visible: vis('manufacturing') },
    ],
  },
  {
    label: 'Sales',
    items: [
      { to: '/sales/orders', label: 'Orders', icon: ShoppingCart, visible: vis('sales') },
      { to: '/sales/quotes', label: 'Quotes', icon: Tag, visible: vis('sales') },
      { to: '/sales/shipments', label: 'Shipments', icon: Truck, visible: vis('sales') },
      { to: '/sales/returns', label: 'Returns / RMA', icon: Undo2, visible: vis('sales') },
      { to: '/sales/feedback', label: 'Customer Feedback', icon: MessageSquareQuote, visible: vis('sales') },
    ],
  },
  {
    label: 'Quality / ISO',
    items: [
      { to: '/iso/documents', label: 'Documents', icon: FileText, visible: vis('sops') },
      { to: '/iso/training', label: 'Training', icon: GraduationCap, visible: vis('sops') },
      { to: '/iso/calibration', label: 'Calibration', icon: Wrench, visible: vis('sops') },
      { to: '/iso/suppliers', label: 'Supplier qual.', icon: Building2, visible: vis('sops') },
      { to: '/iso/ncr-capa', label: 'NCR / CAPA', icon: AlertTriangle, visible: vis('sops') },
      { to: '/iso/audits', label: 'Audits', icon: ClipboardCheck, visible: vis('sops') },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/marketing/sales-marketing', label: 'Sales & Marketing', icon: TrendingUp, visible: vis('marketing') },
      { to: '/marketing/dashboard', label: 'Marketing dashboard', icon: Megaphone, visible: vis('marketing') },
      { to: '/marketing/web-analytics', label: 'Web analytics', icon: Globe, visible: vis('marketing') },
      { to: '/marketing/social-media', label: 'Social media', icon: Share2, visible: vis('marketing') },
      { to: '/marketing/competitors', label: 'Competitors', icon: Target, visible: vis('marketing') },
      { to: '/marketing/affiliates', label: 'Affiliates', icon: Handshake, visible: vis('marketing') },
      { to: '/marketing/advertising', label: 'Advertising', icon: BadgeDollarSign, visible: vis('marketing') },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { to: '/intel/forecast', label: 'Forecast', icon: LineChart, visible: vis('intelligence') },
      { to: '/intel/kpis', label: 'KPIs', icon: Gauge, visible: vis('intelligence') },
    ],
  },
  // Company documents and reference info. Distinct from Settings
  // below, which is system access (integrations, roles, audit).
  {
    label: 'Admin',
    items: [
      { to: '/admin/templates', label: 'Templates', icon: Files, visible: vis('company_admin') },
      { to: '/admin/company', label: 'Company profile', icon: Briefcase, visible: vis('company_admin') },
      { to: '/admin/licenses', label: 'Licenses & insurance', icon: BadgeCheck, visible: vis('company_admin') },
      { to: '/admin/contracts', label: 'Contracts', icon: FileSignature, visible: vis('company_admin') },
      { to: '/admin/contacts', label: 'Key contacts', icon: BookUser, visible: vis('company_admin') },
      { to: '/admin/brand', label: 'Brand kit', icon: Palette, visible: vis('company_admin') },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/integrations', label: 'Integrations', icon: Plug, visible: vis('integrations') },
      { to: '/users', label: 'Roles & users', icon: Users, visible: isSuperAdmin },
      { to: '/admin/card-mapping', label: 'Card mapping', icon: CreditCard, visible: isSuperAdmin },
      { to: '/audit', label: 'Audit log', icon: ScrollText, visible: vis('audit') },
    ],
  },
];

export function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { role, profile } = useAuth();
  const sectionAccess = profile?.section_access;
  const navContext: NavContext = { hasOnlineAccounts: useHasOnlineAccounts() };
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsedFromStorage());
  // Per-item expansion state for rows with `renderChildren`. We
  // store the *collapsed* set (vs expanded) so the default is
  // "children visible" — same polarity as sections above, which
  // matches the mental model "I have to opt out of seeing them".
  const [collapsedItems, setCollapsedItems] = useState<Set<string>>(
    () => readCollapsedItemsFromStorage(),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...collapsed]));
    } catch {
      /* localStorage may be full / disabled (private mode, quota) —
         fall through; the in-memory state is still authoritative. */
    }
  }, [collapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        COLLAPSED_ITEMS_STORAGE_KEY,
        JSON.stringify([...collapsedItems]),
      );
    } catch {
      /* see note on COLLAPSED_STORAGE_KEY write above. */
    }
  }, [collapsedItems]);

  useEffect(() => {
    const activeSection = NAV.find(
      (section) =>
        section.items.some((item) => pathMatchesNav(item.to, pathname)) ||
        section.matchesPath?.(pathname),
    );
    if (!activeSection) return;
    setCollapsed((prev) => {
      if (!prev.has(activeSection.label)) return prev;
      const next = new Set(prev);
      next.delete(activeSection.label);
      return next;
    });
  }, [pathname]);

  // When the user navigates into a route that lives "under" a
  // parent nav item (e.g. /catalog/items/kind/raw_material under
  // /catalog/items), auto-expand that parent so the active child
  // row is actually visible. Without this a deep-link would leave
  // the active child hidden under a collapsed parent.
  useEffect(() => {
    const activeParent = NAV.flatMap((section) => section.items).find(
      (item) =>
        item.renderChildren &&
        (pathname === item.to || pathname.startsWith(`${item.to}/`)),
    );
    if (!activeParent) return;
    setCollapsedItems((prev) => {
      if (!prev.has(activeParent.to)) return prev;
      const next = new Set(prev);
      next.delete(activeParent.to);
      return next;
    });
  }, [pathname]);

  const toggleSection = (label: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const toggleItem = (key: string) => {
    setCollapsedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <>
      <div className="px-5 pb-6 pt-7 md:block hidden">
        <Logo variant="primary" tone="humus" className="h-7" />
      </div>
      <div className="px-5 pb-6 pt-2 md:hidden">
        <Logo variant="primary" tone="humus" className="h-6" />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        {NAV.map((section) => {
          const visibleItems = section.items.filter((item) =>
            item.visible(role, sectionAccess, navContext),
          );
          if (!visibleItems.length) return null;
          const isCollapsed = collapsed.has(section.label);
          const sectionId = `sidebar-section-${section.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
          return (
            <div key={section.label} className="mb-5">
              {/*
               * Section headers carry the Atmosphere-dark accent so the
               * nav scans as a set of labelled groups rather than one
               * long grey list. The chevron stays quiet until hover.
               */}
              <button
                type="button"
                onClick={() => toggleSection(section.label)}
                aria-expanded={!isCollapsed}
                aria-controls={sectionId}
                className="group flex w-full items-center justify-between rounded-md px-3 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.1em] text-info transition-colors hover:bg-info-soft/20"
              >
                <span className="flex min-w-0 items-center gap-2 whitespace-nowrap">
                  <span
                    aria-hidden
                    className="h-1 w-1 rounded-full bg-info/70"
                  />
                  {section.label}
                </span>
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 shrink-0 text-info/50 transition-all duration-200 group-hover:text-info',
                    isCollapsed && '-rotate-90',
                  )}
                  strokeWidth={2}
                />
              </button>
              {!isCollapsed && (
                <>
                  <ul id={sectionId} className="mt-1 space-y-0.5">
                    {visibleItems.map((item) => {
                      const hasChildren = !!item.renderChildren;
                      const childrenCollapsed = collapsedItems.has(item.to);
                      const childrenId = `sidebar-item-${item.to.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-children`;
                      return (
                        <li key={item.to}>
                          {/*
                           * The row is rendered as a flex container of
                           * [NavLink, (optional) chevron] so the link
                           * fills the available width and only the
                           * chevron button toggles expansion. We can't
                           * put the chevron INSIDE the NavLink because
                           * NavLink is itself an <a> — nesting a
                           * <button> inside it would be invalid HTML
                           * and React would warn at runtime.
                           */}
                          <div
                            className={cn(
                              'group flex items-center gap-1 rounded-lg pr-1 text-sm transition-colors',
                              // Hover ripples across the whole row so
                              // the chevron + link feel like one
                              // affordance even though they're
                              // separate elements.
                              !hasChildren ? '' : 'hover:bg-humus/5',
                            )}
                          >
                            <NavLink
                              to={item.to}
                              end={item.end}
                              onClick={onNavigate}
                              className={({ isActive }) =>
                                cn(
                                  'flex flex-1 items-center gap-3 rounded-lg px-3 py-2 transition-colors',
                                  isActive || pathMatchesNav(item.to, pathname)
                                    ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground'
                                    : 'text-sidebar-foreground/80 hover:bg-humus/5 hover:text-sidebar-foreground',
                                )
                              }
                            >
                              <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.7} />
                              <span>{item.label}</span>
                              {item.renderBadge && (
                                <span className="ml-auto flex items-center">{item.renderBadge()}</span>
                              )}
                            </NavLink>
                            {hasChildren && (
                              <button
                                type="button"
                                onClick={() => toggleItem(item.to)}
                                aria-expanded={!childrenCollapsed}
                                aria-controls={childrenId}
                                aria-label={`${childrenCollapsed ? 'Expand' : 'Collapse'} ${item.label} sub-list`}
                                title={childrenCollapsed ? `Expand ${item.label}` : `Collapse ${item.label}`}
                                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-sidebar-foreground/40 transition-colors hover:bg-humus/5 hover:text-sidebar-foreground/80"
                              >
                                <ChevronDown
                                  className={cn(
                                    'h-3.5 w-3.5 transition-transform duration-200',
                                    childrenCollapsed && '-rotate-90',
                                  )}
                                  strokeWidth={2}
                                />
                              </button>
                            )}
                          </div>
                          {hasChildren && !childrenCollapsed && (
                            <div id={childrenId} className="mt-0.5">
                              {item.renderChildren!()}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {/*
                   * Extras render OUTSIDE the static <ul> so they can own
                   * their own container — needed for @hello-pangea/dnd,
                   * which won't function correctly if its Droppable is a
                   * `display: contents` zero-size element nested in a list.
                   */}
                  {section.renderExtras?.()}
                </>
              )}
            </div>
          );
        })}
      </nav>

      <div className="flex items-center gap-2 border-t border-sidebar-border px-5 py-4 text-[11px] text-sidebar-foreground/45">
        <span className="relative flex h-1.5 w-1.5" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-bright-moss-dark/50" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-bright-moss-dark" />
        </span>
        Atlas · Internal use only
      </div>
    </>
  );
}

export function Sidebar({ collapsed }: { collapsed?: boolean }) {
  if (collapsed) return null;
  
  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground md:flex">
      <SidebarContent />
    </aside>
  );
}

/**
 * Dynamic task-list tree rendered under "Tasks" in the sidebar.
 * Projects can be grouped into org-shared folders (and
 * sub-folders — migration 86); ungrouped projects render at the top
 * level. Each project row is a NavLink to /projects/:id. RLS already
 * restricts the result set so we render every project the user can
 * read — same view they get on the dashboard. We cap the visible
 * TOP-LEVEL list so a power user with many ungrouped projects doesn't
 * blow out the sidebar height; folders always show their full
 * contents (the user opted into that structure and can collapse it).
 */
const SIDEBAR_PROJECT_LIMIT = 12;

/** localStorage key for the set of COLLAPSED folder ids (default = expanded). */
const PROJECT_FOLDERS_COLLAPSED_KEY = 'atlas:sidebar:projectFolders:collapsed';

interface FolderNode {
  folder: ProjectFolder;
  depth: number;
  children: FolderNode[];
}

/**
 * Build the folder tree from the flat rows. Folders whose parent isn't
 * in the result set (mid-refetch orphans) fall back to the top level so
 * nothing silently disappears; cycles are prevented server-side by
 * tg_project_folders_no_cycles.
 */
function buildFolderTree(folders: ProjectFolder[]): FolderNode[] {
  const ids = new Set(folders.map((f) => f.id));
  const byParent = new Map<string | null, ProjectFolder[]>();
  for (const f of folders) {
    const parent = f.parent_id && ids.has(f.parent_id) ? f.parent_id : null;
    const arr = byParent.get(parent) ?? [];
    arr.push(f);
    byParent.set(parent, arr);
  }
  const build = (parentId: string | null, depth: number): FolderNode[] =>
    (byParent.get(parentId) ?? []).map((f) => ({
      folder: f,
      depth,
      children: build(f.id, depth + 1),
    }));
  return build(null, 0);
}

/** Depth-first flatten, used by the "Move to folder" menus. */
function flattenFolderTree(nodes: FolderNode[]): FolderNode[] {
  const out: FolderNode[] = [];
  const walk = (list: FolderNode[]) => {
    for (const node of list) {
      out.push(node);
      walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

type FolderEdit =
  | { mode: 'create'; parentId: string | null }
  | { mode: 'rename'; folderId: string };

/**
 * Live count on the "Ticket inbox" row: open tickets routed to me, plus
 * (for the triage tier) open tickets nobody has routed yet. Shares the
 * inbox page's query and realtime channel, so it is one request per
 * minute at most.
 */
function TicketInboxBadge() {
  const { role, profile } = useAuth();
  const canTriage = role === 'super_admin' || role === 'admin' || role === 'manager';
  useTicketsRealtime(!!profile);
  const { data } = useTickets({ enabled: !!profile });
  const { counts } = useTicketViews(data, canTriage);
  if (!data || counts.inbox === 0) return null;
  return (
    <span
      className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-fresh-salmon-dark px-1.5 text-[10px] font-semibold leading-none text-linen"
      aria-label={`${counts.inbox} open tickets in your inbox`}
    >
      {counts.inbox > 99 ? '99+' : counts.inbox}
    </span>
  );
}

/** Cap on the engineering projects listed under the section header. */
const SIDEBAR_ENG_PROJECT_LIMIT = 8;

/**
 * Active engineering projects under "Engineering", newest first, so
 * the team can jump straight into a part's file drive. Cancelled and
 * archived projects stay on the dashboard only.
 */
function EngineeringExtras() {
  const { role, profile } = useAuth();
  const allowed = canRead(role, 'engineering', profile?.section_access);
  const { data: projects } = useEngProjects({ includeArchived: false, enabled: allowed });
  if (!allowed) return null;
  const active = (projects ?? []).filter((p) => p.status !== 'cancelled');
  const visible = active.slice(0, SIDEBAR_ENG_PROJECT_LIMIT);
  const overflow = Math.max(0, active.length - visible.length);
  if (visible.length === 0) return null;
  return (
    <div className="mt-0.5 space-y-0.5" role="list">
      {visible.map((p) => (
        <NavLink
          key={p.id}
          to={`/engineering/${p.id}`}
          role="listitem"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 rounded-lg py-1.5 pl-9 pr-3 text-xs transition-colors',
              isActive
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-sidebar-foreground/70 hover:bg-humus/5 hover:text-sidebar-foreground',
            )
          }
        >
          <span className="shrink-0 font-mono text-[10px] text-sidebar-foreground/45">{p.code}</span>
          <span className="truncate">{p.name}</span>
        </NavLink>
      ))}
      {overflow > 0 && (
        <NavLink
          to="/engineering"
          className="block rounded-lg py-1.5 pl-9 pr-3 text-[11px] text-sidebar-foreground/50 hover:text-sidebar-foreground/80"
        >
          +{overflow} more on the dashboard
        </NavLink>
      )}
    </div>
  );
}

function ProjectsExtras() {
  const { pathname } = useLocation();
  // `includeArchived: true` keeps this query sharing a cache entry with
  // the dashboard. The sidebar itself only renders the active subset,
  // and (since folders) position writes are per-sibling-group so the
  // archived rows no longer need to ride along in reorder payloads.
  const { data: allProjects, isLoading: projectsLoading } = useProjects({
    includeArchived: true,
  });
  const { data: folders, isLoading: foldersLoading } = useProjectFolders();
  const reorder = useReorderProjects();
  const moveToFolder = useMoveProjectToFolder();
  const createFolder = useCreateProjectFolder();
  const renameFolder = useRenameProjectFolder();
  const deleteFolder = useDeleteProjectFolder();

  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(() =>
    readStringSetFromStorage(PROJECT_FOLDERS_COLLAPSED_KEY),
  );
  const [edit, setEdit] = useState<FolderEdit | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        PROJECT_FOLDERS_COLLAPSED_KEY,
        JSON.stringify([...collapsedFolders]),
      );
    } catch {
      /* see note on COLLAPSED_STORAGE_KEY write above. */
    }
  }, [collapsedFolders]);

  const folderList = useMemo(() => folders ?? [], [folders]);
  const tree = useMemo(() => buildFolderTree(folderList), [folderList]);
  const flatFolders = useMemo(() => flattenFolderTree(tree), [tree]);

  const projects = (allProjects ?? []).filter((p) => !p.archived_at);
  // Group by folder, preserving the position-sorted query order within
  // each group. A stale folder_id (folder deleted mid-cache) falls back
  // to the top level rather than hiding the project.
  const folderIds = new Set(folderList.map((f) => f.id));
  const projectsByFolder = new Map<string | null, ProjectWithMembers[]>();
  for (const p of projects) {
    const key = p.folder_id && folderIds.has(p.folder_id) ? p.folder_id : null;
    const arr = projectsByFolder.get(key) ?? [];
    arr.push(p);
    projectsByFolder.set(key, arr);
  }
  const rootProjects = projectsByFolder.get(null) ?? [];
  const visibleRoot = rootProjects.slice(0, SIDEBAR_PROJECT_LIMIT);
  const overflow = Math.max(0, rootProjects.length - visibleRoot.length);

  /** Un-collapse a folder and every ancestor above it. */
  const expandChain = (folderId: string) => {
    const byId = new Map(folderList.map((f) => [f.id, f]));
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      let cur: string | null = folderId;
      let guard = 0;
      while (cur && guard < 20) {
        next.delete(cur);
        cur = byId.get(cur)?.parent_id ?? null;
        guard += 1;
      }
      return next.size === prev.size ? prev : next;
    });
  };

  // Auto-expand the folder chain containing the active project so a
  // deep link never leaves the active row hidden under a collapsed
  // folder — mirrors the section / parent-item auto-expand above.
  const activeProjectId = pathname.match(/^\/projects\/([^/]+)/)?.[1];
  const activeFolderId = activeProjectId
    ? ((allProjects ?? []).find((p) => p.id === activeProjectId)?.folder_id ??
      null)
    : null;
  useEffect(() => {
    if (activeFolderId) expandChain(activeFolderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolderId]);

  if (projectsLoading || foldersLoading) {
    return (
      <p className="px-3 py-1 text-[11px] text-sidebar-foreground/40">
        Loading projects…
      </p>
    );
  }

  const onMutationError = (err: unknown) => {
    toast.error(err instanceof Error ? err.message : 'Something went wrong');
  };

  const toggleFolder = (id: string) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const startCreate = (parentId: string | null) => {
    if (parentId) expandChain(parentId);
    setEdit({ mode: 'create', parentId });
  };

  const handleCreate = (parentId: string | null, name: string) => {
    setEdit(null);
    createFolder.mutate({ name, parentId }, { onError: onMutationError });
  };

  const handleRename = (folderId: string, name: string) => {
    setEdit(null);
    renameFolder.mutate({ id: folderId, name }, { onError: onMutationError });
  };

  const handleDelete = (folder: ProjectFolder) => {
    const ok = window.confirm(
      `Delete folder “${folder.name}”? Projects and sub-folders inside it move up one level.`,
    );
    if (!ok) return;
    deleteFolder.mutate({ id: folder.id }, { onError: onMutationError });
  };

  /** "Move to folder" menu action — appends the project to the end of
   *  the destination list and expands it so the user sees the landing
   *  spot. */
  const handleMoveProject = (projectId: string, folderId: string | null) => {
    const dest =
      folderId === null ? rootProjects : (projectsByFolder.get(folderId) ?? []);
    const orderedIds = [
      ...dest.filter((p) => p.id !== projectId).map((p) => p.id),
      projectId,
    ];
    moveToFolder.mutate(
      { projectId, folderId, orderedIds },
      { onError: onMutationError },
    );
    if (folderId) expandChain(folderId);
  };

  /** Resolve a droppableId back to its folder + the rows it rendered. */
  const listFor = (droppableId: string) => {
    if (droppableId === 'sidebar-projects-root')
      return { folderId: null as string | null, items: visibleRoot };
    const folderId = droppableId.replace(/^sidebar-projects-folder:/, '');
    return { folderId, items: projectsByFolder.get(folderId) ?? [] };
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    const src = listFor(source.droppableId);
    const dst = listFor(destination.droppableId);
    // The top-level list is capped at SIDEBAR_PROJECT_LIMIT rows, so any
    // ordering write that targets it has to concat the hidden tail —
    // otherwise rows past the cap would drift to the top. Positions are
    // per-sibling-group now, so folders never need a tail.
    const rootTailIds = rootProjects.slice(visibleRoot.length).map((p) => p.id);

    if (src.folderId === dst.folderId) {
      const next = src.items.slice();
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      const orderedIds = [
        ...next.map((p) => p.id),
        ...(dst.folderId === null ? rootTailIds : []),
      ];
      reorder.mutate({ orderedIds }, { onError: onMutationError });
      return;
    }

    // Cross-list drop: move into the destination folder at the drop
    // index. set_project_folder + set_project_positions run server-side
    // in one mutation (see useMoveProjectToFolder).
    const destIds = dst.items.map((p) => p.id);
    destIds.splice(destination.index, 0, draggableId);
    moveToFolder.mutate(
      {
        projectId: draggableId,
        folderId: dst.folderId,
        orderedIds: [
          ...destIds,
          ...(dst.folderId === null ? rootTailIds : []),
        ],
      },
      { onError: onMutationError },
    );
  };

  // Rendered as a sibling of the section's static <ul>, NOT inside it —
  // @hello-pangea/dnd needs real, measurable Droppable containers (see
  // the note on renderExtras above). Folder blocks are plain divs; only
  // the per-folder project lists are Droppables, and they are never
  // nested inside each other, which keeps the library's hit-testing
  // reliable.
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="mt-0.5 space-y-0.5">
        {tree.map((node) => (
          <FolderBlock
            key={node.folder.id}
            node={node}
            projectsByFolder={projectsByFolder}
            collapsedFolders={collapsedFolders}
            onToggle={toggleFolder}
            edit={edit}
            onCancelEdit={() => setEdit(null)}
            onStartCreate={startCreate}
            onStartRename={(id) => setEdit({ mode: 'rename', folderId: id })}
            onCreate={handleCreate}
            onRename={handleRename}
            onDelete={handleDelete}
            flatFolders={flatFolders}
            onMoveProject={handleMoveProject}
          />
        ))}
        {edit?.mode === 'create' && edit.parentId === null && (
          <FolderNameInput
            depth={0}
            onCommit={(name) => handleCreate(null, name)}
            onCancel={() => setEdit(null)}
          />
        )}
        <ProjectListDroppable
          droppableId="sidebar-projects-root"
          projects={visibleRoot}
          depth={0}
          currentFolderId={null}
          flatFolders={flatFolders}
          onMoveProject={handleMoveProject}
        />
        {projects.length === 0 && folderList.length === 0 && (
          <p className="px-3 py-1 text-[11px] italic text-sidebar-foreground/40">
            No projects yet
          </p>
        )}
        {overflow > 0 && (
          <NavLink
            to="/tasks"
            className="block rounded-lg py-1.5 pl-9 pr-3 text-[11px] text-sidebar-foreground/50 hover:text-sidebar-foreground/80"
          >
            +{overflow} more in My tasks
          </NavLink>
        )}
        <button
          type="button"
          onClick={() => startCreate(null)}
          className="flex w-full items-center gap-2 rounded-lg py-1.5 pl-9 pr-3 text-[11px] text-sidebar-foreground/45 transition-colors hover:bg-humus/5 hover:text-sidebar-foreground/80"
        >
          <FolderPlus className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} />
          New folder
        </button>
      </div>
    </DragDropContext>
  );
}

/**
 * One folder row + (when expanded) its sub-folders and project list.
 * Recursive — depth drives the indentation. The row itself is the
 * expand/collapse toggle; folder management lives in the hover "…"
 * menu so a misclick can't fire anything destructive.
 */
function FolderBlock(props: {
  node: FolderNode;
  projectsByFolder: Map<string | null, ProjectWithMembers[]>;
  collapsedFolders: Set<string>;
  onToggle: (id: string) => void;
  edit: FolderEdit | null;
  onCancelEdit: () => void;
  onStartCreate: (parentId: string | null) => void;
  onStartRename: (folderId: string) => void;
  onCreate: (parentId: string | null, name: string) => void;
  onRename: (folderId: string, name: string) => void;
  onDelete: (folder: ProjectFolder) => void;
  flatFolders: FolderNode[];
  onMoveProject: (projectId: string, folderId: string | null) => void;
}) {
  const { node } = props;
  const folder = node.folder;
  const isCollapsed = props.collapsedFolders.has(folder.id);
  const directProjects = props.projectsByFolder.get(folder.id) ?? [];
  const isRenaming =
    props.edit?.mode === 'rename' && props.edit.folderId === folder.id;
  const FolderIcon = isCollapsed ? Folder : FolderOpen;

  return (
    <div>
      {isRenaming ? (
        <FolderNameInput
          depth={node.depth}
          initial={folder.name}
          onCommit={(name) => props.onRename(folder.id, name)}
          onCancel={props.onCancelEdit}
        />
      ) : (
        <div className="group/folder relative">
          <button
            type="button"
            onClick={() => props.onToggle(folder.id)}
            aria-expanded={!isCollapsed}
            style={{ paddingLeft: 12 + node.depth * 12 }}
            className="flex w-full items-center gap-1.5 rounded-lg py-1.5 pr-8 text-xs text-sidebar-foreground/70 transition-colors hover:bg-humus/5 hover:text-sidebar-foreground"
          >
            <ChevronDown
              className={cn(
                'h-3 w-3 shrink-0 text-sidebar-foreground/40 transition-transform duration-200',
                isCollapsed && '-rotate-90',
              )}
              strokeWidth={2}
            />
            <FolderIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} />
            <span className="truncate font-medium">{folder.name}</span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Folder actions for ${folder.name}`}
                className="absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-sidebar-foreground/40 opacity-0 transition-opacity hover:bg-humus/10 hover:text-sidebar-foreground/80 group-hover/folder:opacity-100 data-[state=open]:opacity-100"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="right" className="text-xs">
              <DropdownMenuItem onSelect={() => props.onStartCreate(folder.id)}>
                <FolderPlus className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                New sub-folder
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => props.onStartRename(folder.id)}>
                <Pencil className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                Rename folder
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => props.onDelete(folder)}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      {!isCollapsed && (
        <div className="space-y-0.5">
          {node.children.map((child) => (
            <FolderBlock key={child.folder.id} {...props} node={child} />
          ))}
          {props.edit?.mode === 'create' &&
            props.edit.parentId === folder.id && (
              <FolderNameInput
                depth={node.depth + 1}
                onCommit={(name) => props.onCreate(folder.id, name)}
                onCancel={props.onCancelEdit}
              />
            )}
          <ProjectListDroppable
            droppableId={`sidebar-projects-folder:${folder.id}`}
            projects={directProjects}
            depth={node.depth + 1}
            currentFolderId={folder.id}
            flatFolders={props.flatFolders}
            onMoveProject={props.onMoveProject}
            showEmptyHint
          />
        </div>
      )}
    </div>
  );
}

/**
 * The draggable project rows for one folder (or the top level). Every
 * instance is its own Droppable so a drag can land in any EXPANDED
 * list; the per-row "…" menu covers moves into collapsed folders.
 */
function ProjectListDroppable(props: {
  droppableId: string;
  projects: ProjectWithMembers[];
  depth: number;
  currentFolderId: string | null;
  flatFolders: FolderNode[];
  onMoveProject: (projectId: string, folderId: string | null) => void;
  /** Render the italic "No projects" row when empty — gives an empty
   *  folder a visible (and droppable) body. */
  showEmptyHint?: boolean;
}) {
  const textIndent = 36 + props.depth * 12;
  const handleLeft = 8 + props.depth * 12;
  return (
    <Droppable droppableId={props.droppableId}>
      {(dropProvided) => (
        <div
          ref={dropProvided.innerRef}
          {...dropProvided.droppableProps}
          role="list"
          className="space-y-0.5"
        >
          {props.projects.map((p, idx) => (
            <Draggable key={p.id} draggableId={p.id} index={idx}>
              {(dragProvided, snapshot) => (
                <div
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                  role="listitem"
                  className={cn(
                    'group/proj relative',
                    snapshot.isDragging &&
                      'rounded-lg bg-sidebar-accent/80 shadow-lg ring-1 ring-sidebar-accent',
                  )}
                >
                  <NavLink
                    to={`/projects/${p.id}`}
                    style={{ paddingLeft: textIndent }}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-3 rounded-lg py-1.5 pr-8 text-xs transition-colors',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/70 hover:bg-humus/5 hover:text-sidebar-foreground',
                      )
                    }
                  >
                    <span className="truncate">{p.name}</span>
                  </NavLink>
                  <span
                    {...dragProvided.dragHandleProps}
                    role="button"
                    aria-label={`Reorder ${p.name}`}
                    style={{ left: handleLeft }}
                    className="absolute top-1/2 -translate-y-1/2 cursor-grab rounded p-0.5 text-sidebar-foreground/30 opacity-0 transition-opacity hover:bg-humus/10 hover:text-sidebar-foreground/80 active:cursor-grabbing group-hover/proj:opacity-100"
                  >
                    <GripVertical className="h-3 w-3" strokeWidth={1.6} />
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label={`Move ${p.name} to a folder`}
                        className="absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-sidebar-foreground/40 opacity-0 transition-opacity hover:bg-humus/10 hover:text-sidebar-foreground/80 group-hover/proj:opacity-100 data-[state=open]:opacity-100"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      side="right"
                      className="max-h-72 overflow-y-auto text-xs"
                    >
                      <DropdownMenuLabel>Move to folder</DropdownMenuLabel>
                      <DropdownMenuItem
                        disabled={props.currentFolderId === null}
                        onSelect={() => props.onMoveProject(p.id, null)}
                      >
                        <FolderKanban className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                        Top level
                      </DropdownMenuItem>
                      {props.flatFolders.map((f) => (
                        <DropdownMenuItem
                          key={f.folder.id}
                          disabled={props.currentFolderId === f.folder.id}
                          onSelect={() =>
                            props.onMoveProject(p.id, f.folder.id)
                          }
                        >
                          <span
                            aria-hidden
                            style={{ width: f.depth * 12 }}
                            className="shrink-0"
                          />
                          <Folder className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                          <span className="truncate">{f.folder.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </Draggable>
          ))}
          {dropProvided.placeholder}
          {props.showEmptyHint && props.projects.length === 0 && (
            <p
              style={{ paddingLeft: textIndent }}
              className="py-1 pr-3 text-[11px] italic text-sidebar-foreground/35"
            >
              No projects
            </p>
          )}
        </div>
      )}
    </Droppable>
  );
}

/**
 * Inline name editor used for both "New folder" and "Rename folder".
 * Enter commits, Escape cancels, blur commits (or cancels when the
 * value is empty / unchanged) — same lightweight pattern as Asana's
 * sidebar so the flow never needs a modal.
 */
function FolderNameInput(props: {
  depth: number;
  initial?: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(props.initial ?? '');
  const cancelled = useRef(false);
  return (
    <div
      style={{ paddingLeft: 12 + props.depth * 12 }}
      className="py-0.5 pr-3"
    >
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Folder name"
        aria-label="Folder name"
        className="w-full rounded-md border border-sidebar-border bg-background px-2 py-1 text-xs text-sidebar-foreground placeholder:text-sidebar-foreground/40 focus:border-sidebar-foreground/40 focus:outline-none"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            cancelled.current = true;
            e.currentTarget.blur();
          }
        }}
        onBlur={() => {
          if (cancelled.current) {
            cancelled.current = false;
            props.onCancel();
            return;
          }
          const name = value.trim();
          if (!name || name === props.initial) props.onCancel();
          else props.onCommit(name);
        }}
      />
    </div>
  );
}

// ─────────────────── Item kinds sub-list ───────────────────────────
//
// Collapsible list of every Kind in the org's catalog, rendered
// directly underneath the "Items" row in the Catalog section. Each
// row links to /catalog/items/kind/:slug, which `KindItemsPage`
// resolves into a per-kind variant of the items grid. Using the
// live `useItemKinds()` catalog means new kinds a super admin
// mints from the KindPicker show up in the sidebar without a code
// change.
//
// Sorting mirrors the kinds catalog order (position, then label) so
// the baseline four (raw_material, wip, finished_good, consumable)
// stay pinned at the top and custom kinds append underneath. The
// styling matches the project sub-list above for visual continuity
// — slightly smaller text, indented past the parent icon, with the
// active row picking up the same accent treatment as the rest of
// the sidebar.

/**
 * Tailwind class names for the coloured swatch we render next to
 * each kind row. Mirrors the equivalents in `KindPicker` /
 * `NewItemDialog` so the same palette token reads the same way
 * everywhere a kind colour shows up. Kept as a flat record so the
 * Tailwind JIT picks up the literal class names at build time.
 */
const KIND_DOT_CLASS: Record<ItemKindColor, string> = {
  slate: 'bg-slate-400',
  red: 'bg-red-400',
  orange: 'bg-orange-400',
  amber: 'bg-amber-400',
  green: 'bg-green-400',
  teal: 'bg-teal-400',
  blue: 'bg-blue-400',
  indigo: 'bg-indigo-400',
  purple: 'bg-purple-400',
  pink: 'bg-pink-400',
};

// ─────────────────── PTO sub-list ──────────────────────────────────
//
// Two fixed rows underneath the "PTO" entry in the HR section — the
// self-service surface (My PTO) and the approver surface (Manage PTO).
// Same render-children plumbing as Loans → Family / SBA so the chevron,
// collapse persistence, and active-route auto-expand come for free.
//
// Manage PTO is gated to approvers (super_admin / admin / manager) —
// mirrors the route gate in App.tsx and pto_can_approve() in migration 95.
// My PTO uses `end` on its NavLink so /hr/pto/manage does not also light
// up the My PTO row (path is a prefix of the manage route).

const PTO_SUB_ITEMS: {
  to: string;
  label: string;
  icon: LucideIcon;
  visible: (role: Role | null, sectionAccess?: SectionAccess | null) => boolean;
  /** Exact-match only — needed when this path is a prefix of a sibling. */
  end?: boolean;
}[] = [
  {
    to: '/hr/pto',
    label: 'My PTO',
    icon: Palmtree,
    visible: vis('pto'),
    end: true,
  },
  {
    to: '/hr/pto/manage',
    label: 'Manage PTO',
    icon: ClipboardCheck,
    visible: (r) => canApprovePto(r),
  },
];

function PtoExtras() {
  const { role, profile } = useAuth();
  const items = PTO_SUB_ITEMS.filter((item) => item.visible(role, profile?.section_access));
  if (items.length === 0) return null;
  return (
    <ul role="list" className="mt-0.5 space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-2.5 rounded-lg py-1.5 pl-9 pr-3 text-xs transition-colors',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground/70 hover:bg-humus/5 hover:text-sidebar-foreground',
              )
            }
          >
            <item.icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} />
            <span className="truncate">{item.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

// ─────────────────── Production sub-list ───────────────────────────
//
// Fixed rows underneath "Production" in the Manufacturing section,
// using the same render-children plumbing as PTO and Loans.

const PRODUCTION_SUB_ITEMS: {
  to: string;
  label: string;
  icon: LucideIcon;
  visible: (role: Role | null, sectionAccess?: SectionAccess | null) => boolean;
}[] = [
  {
    to: '/mfg/production/employee-matrix',
    label: 'Employee matrix',
    icon: Grid3x3,
    visible: vis('manufacturing'),
  },
  {
    to: '/mfg/production/units',
    label: 'Unit log',
    icon: ScanBarcode,
    visible: vis('manufacturing'),
  },
];

function ProductionExtras() {
  const { role, profile } = useAuth();
  const items = PRODUCTION_SUB_ITEMS.filter((item) => item.visible(role, profile?.section_access));
  if (items.length === 0) return null;
  return (
    <ul role="list" className="mt-0.5 space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-2.5 rounded-lg py-1.5 pl-9 pr-3 text-xs transition-colors',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground/70 hover:bg-humus/5 hover:text-sidebar-foreground',
              )
            }
          >
            <item.icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} />
            <span className="truncate">{item.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

// ─────────────────── Loans sub-list ────────────────────────────────
//
// Two fixed rows underneath the "Loans" entry in the Financials
// section — one per loan instrument we currently track (Family note
// + SBA-backed bank loan). We keep the same render-children plumbing
// as Items → kinds so the chevron / collapse state, persistence, and
// active-route auto-expand all come for free.
//
// Each sub-item carries its own `visible(role)` predicate because
// external lenders are scoped to specific loans. Today that means:
//   - Family loan → visible to anyone with `loans` module read
//     (super_admin AND external_lender)
//   - SBA loan    → super_admin only (external lenders never see
//     SBA terms)
// The corresponding route gates in App.tsx and the storage-RLS slug
// allowlist in migration 60 enforce the same split server-side.

const LOAN_SUB_ITEMS: {
  to: string;
  label: string;
  icon: LucideIcon;
  visible: (role: Role | null, sectionAccess?: SectionAccess | null) => boolean;
}[] = [
  {
    to: '/financials/loans/family',
    label: 'Family loan',
    icon: Users,
    visible: vis('loans'),
  },
  {
    to: '/financials/loans/sba',
    label: 'SBA loan',
    icon: Landmark,
    visible: isSuperAdmin,
  },
];

function LoansExtras() {
  const { role, profile } = useAuth();
  const items = LOAN_SUB_ITEMS.filter((item) => item.visible(role, profile?.section_access));
  if (items.length === 0) return null;
  return (
    <ul role="list" className="mt-0.5 space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-2.5 rounded-lg py-1.5 pl-9 pr-3 text-xs transition-colors',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground/70 hover:bg-humus/5 hover:text-sidebar-foreground',
              )
            }
          >
            <item.icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} />
            <span className="truncate">{item.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

// ─────────────────── Software sub-list ─────────────────────────────
//
// Subscriptions (what the company pays for) and Accounts (every online
// account and its logins). Accounts only shows for super admins and
// people an account has been shared with, so new employees never see it.

function SoftwareExtras() {
  const { role, profile } = useAuth();
  const hasOnlineAccounts = useHasOnlineAccounts();
  const items = [
    {
      to: '/catalog/software/subscriptions',
      label: 'Subscriptions',
      icon: Repeat,
      visible: canRead(role, 'catalog', profile?.section_access),
    },
    {
      to: '/catalog/software/accounts',
      label: 'Accounts',
      icon: KeyRound,
      visible: hasOnlineAccounts,
    },
  ].filter((item) => item.visible);
  if (items.length === 0) return null;
  return (
    <ul role="list" className="mt-0.5 space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-2.5 rounded-lg py-1.5 pl-9 pr-3 text-xs transition-colors',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground/70 hover:bg-humus/5 hover:text-sidebar-foreground',
              )
            }
          >
            <item.icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} />
            <span className="truncate">{item.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

// ─────────────────── Equipment categories sub-list ─────────────────
//
// The four fixed equipment categories under "Equipment" in Catalog,
// each linking to /catalog/equipment/category/:category.

function EquipmentCategoryExtras() {
  return (
    <ul role="list" className="mt-0.5 space-y-0.5">
      {EQUIPMENT_CATEGORIES.map((category) => {
        const { icon: Icon, label } = EQUIPMENT_CATEGORY_META[category];
        return (
          <li key={category}>
            <NavLink
              to={`/catalog/equipment/category/${category}`}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-2.5 rounded-lg py-1.5 pl-9 pr-3 text-xs transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground/70 hover:bg-humus/5 hover:text-sidebar-foreground',
                )
              }
            >
              <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} />
              <span className="truncate">{label}</span>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

function ItemKindsExtras() {
  const { data: kinds, isLoading } = useItemKinds();

  if (isLoading) {
    return (
      <p className="px-3 py-1 pl-9 text-[11px] text-sidebar-foreground/40">Loading kinds…</p>
    );
  }

  const sorted = [...(kinds ?? [])].sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.label.localeCompare(b.label);
  });

  if (sorted.length === 0) {
    return (
      <p className="px-3 py-1 pl-9 text-[11px] italic text-sidebar-foreground/40">
        No kinds yet
      </p>
    );
  }

  return (
    <ul role="list" className="mt-0.5 space-y-0.5">
      {sorted.map((kind) => (
        <li key={kind.id}>
          <NavLink
            to={`/catalog/items/kind/${kind.slug}`}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-2.5 rounded-lg py-1.5 pl-9 pr-3 text-xs transition-colors',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground/70 hover:bg-humus/5 hover:text-sidebar-foreground',
              )
            }
          >
            <span
              aria-hidden
              className={cn(
                'h-2 w-2 shrink-0 rounded-full',
                KIND_DOT_CLASS[kind.color] ?? KIND_DOT_CLASS.slate,
              )}
            />
            <span className="truncate">{kind.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
